import { z } from 'zod';

/**
 * Risk proximity to any loss limit. Drives screens 06 (active), 14 (warning),
 * 12 (pass) and 13 (fail).
 *  - ok:       comfortably within limits
 *  - warn75:   at 75% of a loss limit — amber banner
 *  - warn90:   at 90% of a loss limit — red urgent banner, AT RISK
 *  - breached: a limit was crossed — positions auto-flat, challenge ended
 */
export type RiskLevel = 'ok' | 'warn75' | 'warn90' | 'breached';

export type ChallengeCategory = 'Scalping' | 'Options' | 'Intraday';

/**
 * The client surfaces a challenge can be shown on. Admin-configured per
 * challenge; all three by default. The catalogue endpoint filters by the
 * requesting client's platform, so each app sees only its own challenges.
 */
export const CHALLENGE_PLATFORMS = ['web', 'ios', 'android'] as const;
export type ChallengePlatform = (typeof CHALLENGE_PLATFORMS)[number];

/**
 * Mentorship configuration — who mentors and what the trader gets. A
 * mentorship is a coaching SERVICE, not a prize challenge: the mentor
 * reviews every order placed on the app (paper trades only), pinpoints
 * mistakes over live 1-on-1 calls, and coaches the trader to the target.
 *
 *  - mentorName: the expert this program belongs to; leads the card
 *    (different challenges are created per mentor).
 *  - mentorTitle: one-line credential under the name, e.g.
 *    "Options mentor · NISM certified".
 *  - mentorBio: the "About your mentor" intro on the detail page.
 *  - yearsExperience / tradersCoached: the hero stat row.
 *  - expertReview: the mentor personally reviews the orders and contacts the
 *    trader to discuss them, order by order.
 *  - aiReports: AI-generated reports on every trade placed in the window.
 * At least one review option is always on; both can be.
 */
export const MentorshipConfigSchema = z.object({
  mentorName: z.string(),
  mentorTitle: z.string().optional(),
  mentorBio: z.string().optional(),
  yearsExperience: z.number().optional(),
  tradersCoached: z.number().optional(),
  expertReview: z.boolean(),
  aiReports: z.boolean(),
});
export type MentorshipConfig = z.infer<typeof MentorshipConfigSchema>;

export const ChallengeSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.custom<ChallengeCategory>(),
  featured: z.boolean().default(false),
  /** Virtual funding capital, in rupees. */
  capital: z.number(),
  /** Profit target as a fraction (0.10 = +10%). */
  targetPct: z.number(),
  /** Max drawdown as a fraction (0.04 = 4%). */
  maxDrawdownPct: z.number(),
  /** One-time entry fee in real rupees. Legally gated — UI only until counsel. */
  entryFee: z.number(),
  enrolled: z.number(),
  /**
   * Challenge model: standard (pass → funded), free (no entry fee, practice
   * leagues), leaderboard (top-N ranked payouts from a reward pool),
   * mentorship (a fixed trade budget inside a window, then a personal
   * expert/AI review of the orders instead of a funded account).
   */
  challengeKind: z.enum(['standard', 'free', 'leaderboard', 'mentorship']).default('standard'),
  /**
   * Which client platforms this challenge shows on. Absent = all three.
   * Informational on the DTO — the server already filters the catalogue by
   * the requesting client's platform.
   */
  platforms: z.array(z.enum(CHALLENGE_PLATFORMS)).optional(),
  /** Mentorship only: total orders the trader may place in the window. */
  maxTrades: z.number().int().positive().nullable().optional(),
  /** Mentorship only: which reviews the trader gets (expert and/or AI). */
  mentorship: MentorshipConfigSchema.nullable().optional(),
  /** Total reward on offer — funded capital share or leaderboard pool. */
  rewardPool: z.number().optional(),
  /**
   * Trader's cut of funded profits (0.8 = 80%). On the cards this is shown
   * BESIDE the total funded capital, never in place of it — quoting only the
   * trader's slice makes the challenge look smaller than it is.
   */
  fundedSplitPct: z.number().optional(),
  /** Leaderboard payouts go to the top N. */
  rewardTopN: z.number().optional(),
  /** Challenge duration in trading days. */
  durationDays: z.number().optional(),
  /** What's tradable, for the card meta line — e.g. "Indices", "Stocks · F&O". */
  instruments: z.string().optional(),
  /** Admin-controlled: whether this challenge shows a public leaderboard. */
  leaderboardEnabled: z.boolean().optional(),

  /**
   * The rules that actually make one challenge different from another. Every
   * card showed the same six fields with different numbers, which read as one
   * product repeated; these are the admin-set constraints, and a card shows
   * only the ones that are switched on. Optional throughout: absent means
   * "ordinary", so nothing is claimed about a challenge we weren't told about.
   */
  phases: z.number().int().min(1).max(2).optional(),
  payoutCycle: z.enum(['weekly', 'monthly']).optional(),
  /** Restricted session, e.g. { start: '09:30', end: '14:30' } IST. */
  tradingWindow: z.object({ start: z.string(), end: z.string() }).nullable().optional(),
  /** Consistency cap: max share of total profit allowed from one day. */
  consistencyMaxDayPct: z.number().nullable().optional(),
  /** A random instrument is drawn at join (anti-hedging lock). */
  randomDraw: z.boolean().optional(),
  /** Options and futures only — no cash equity. */
  derivativesOnly: z.boolean().optional(),

  /**
   * Availability — what makes a challenge an EVENT rather than a permanent
   * catalogue entry. All optional: absent means always open, unlimited.
   */
  /** Joins open at this time (epoch ms); before it the card reads "starts in…". */
  startsAt: z.number().nullable().optional(),
  /** Joins close at this time (epoch ms); the card counts down to it. */
  endsAt: z.number().nullable().optional(),
  /** Contest cap: total paid seats. */
  maxSeats: z.number().nullable().optional(),
  /** Live remaining seats (server-computed); null when unlimited. */
  seatsLeft: z.number().nullable().optional(),
  /**
   * What winning pays. Absent/'funded' = the funded account (default model).
   * 'voucher' = a gift voucher (label names it, value in ₹); 'custom' = any
   * prize the admin describes (label is the prize, value optional);
   * 'practice' = explicitly NO prize — the run exists purely for practice,
   * and the clients must not promise a funded account or reward.
   */
  reward: z
    .object({
      kind: z.enum(['funded', 'voucher', 'custom', 'practice']),
      label: z.string().optional(),
      value: z.number().optional(),
    })
    .nullable()
    .optional(),
  /**
   * Ranked contest prizes: final leaderboard position decides the tier —
   * rank r wins the first tier with r <= upTo (tiers sorted ascending).
   * 'wallet' credits real ₹ automatically at settlement; 'voucher'/'custom'
   * put the winner on the admin's fulfilment list. Requires endsAt: ranks
   * only become final when the contest actually ends.
   */
  rewardTiers: z
    .array(
      z.object({
        upTo: z.number().int().positive(),
        kind: z.enum(['wallet', 'voucher', 'custom']),
        label: z.string().optional(),
        value: z.number().optional(),
      }),
    )
    .nullable()
    .optional(),
});
export type ChallengeSummary = z.infer<typeof ChallengeSummarySchema>;
export type ChallengeReward = NonNullable<ChallengeSummary['reward']>;
export type RewardTier = NonNullable<ChallengeSummary['rewardTiers']>[number];

export const TradingRulesSchema = z.object({
  profitTarget: z.object({ amount: z.number(), pct: z.number() }),
  maxDailyLoss: z.object({ amount: z.number(), pct: z.number() }),
  maxTotalLoss: z.object({ amount: z.number(), pct: z.number() }),
  minTradingDays: z.number(),
});
export type TradingRules = z.infer<typeof TradingRulesSchema>;

/**
 * Per-account randomized parameters — the anti-abuse mechanic (screen 04).
 * Drawn independently per account so colluding accounts can't line trades up.
 */
export const ChallengeParamsSchema = z.object({
  tradingWindow: z.string(), // "09:30 – 12:00"
  maxPositionLots: z.number(),
  allowedStrikes: z.string(), // "Weekly · ATM ±2"
  expiry: z.string(), // "28 Mar (current)"
});
export type ChallengeParams = z.infer<typeof ChallengeParamsSchema>;

export const ChallengeDetailSchema = ChallengeSummarySchema.extend({
  rating: z.number(),
  ratingCount: z.number(),
  description: z.string(),
  fundedSplitPct: z.number(),
  /**
   * How often a funded trader is paid their profit share after passing.
   * "80% split" says how much; this says when.
   */
  payoutCycle: z.enum(['weekly', 'monthly']).default('monthly'),
  /**
   * How many evaluation phases before funding: 1 (single step) or 2 (the
   * classic verification model). Not every challenge runs two — a one-phase
   * challenge must not advertise a Phase 2 it will never run.
   */
  phases: z.number().int().min(1).max(2).default(2),
  /** Phase 2's profit target. Null when `phases` is 1 — there is no phase 2. */
  phase2TargetPct: z.number().nullable(),
  rules: TradingRulesSchema,
  /** Phase 2's full rule set; null on single-phase (and pre-per-phase rows). */
  rules2: TradingRulesSchema.nullable().optional(),
  /** Phase 2's calendar window; null falls back to durationDays. */
  duration2Days: z.number().nullable().optional(),
  /**
   * Deprecated: the detail page used to preview "your" per-account params
   * here, but they were placeholder values (the real draw happens at
   * enrollment) — and the engine does not enforce them. No longer sent.
   */
  params: ChallengeParamsSchema.optional(),
});
export type ChallengeDetail = z.infer<typeof ChallengeDetailSchema>;

/** Live session state streamed while a challenge is active (screens 06/14/12/13). */
export const ChallengeSessionSchema = z.object({
  challengeId: z.string(),
  name: z.string(),
  phase: z.number(),
  totalPhases: z.number(),
  day: z.number(),
  totalDays: z.number(),
  netPnl: z.number(),
  netPnlPct: z.number(),
  /** The challenge's funded capital — the scope's whole buying-power pool.
   *  Optional: older servers (and the mock fixture) omit it. */
  capital: z.number().optional(),
  /** Banked P&L only — the pass criterion; open positions never count. */
  realizedPnl: z.number(),
  target: z.number(),
  dailyLossUsed: z.number(),
  dailyLossLimit: z.number(),
  totalLossUsed: z.number(),
  totalLossLimit: z.number(),
  tradingDays: z.number(),
  minTradingDays: z.number(),
  /**
   * Mentorship runs only: the fixed trade budget and how much of it is spent.
   * Absent on evaluation challenges (and older servers).
   */
  maxTrades: z.number().nullable().optional(),
  tradesUsed: z.number().optional(),
  /** 'mentorship' when this run is a mentorship program; absent = evaluation. */
  kind: z.enum(['evaluation', 'mentorship']).optional(),
  /** Mentorship runs only: the mentor's name, for the tracking screen. */
  mentorName: z.string().optional(),
  allowedInstrument: z.string(),
  riskLevel: z.custom<RiskLevel>(),
  /** Enrollment lifecycle — drives 06 (active) vs result variants 12/13. */
  status: z.enum(['pending_payment', 'awaiting_draw', 'active', 'passed', 'failed', 'cancelled']),
  /** e.g. "MAX DAILY LOSS BREACHED" — the fail headline on screen 13. */
  endReason: z.string().nullable(),
});

/** One row of a challenge leaderboard, ranked by return. */
export const LeaderboardEntrySchema = z.object({
  rank: z.number(),
  /** Display name — real name or a masked fallback; never the raw phone. */
  name: z.string(),
  pnl: z.number(),
  pnlPct: z.number(),
  status: z.enum(['active', 'passed', 'failed']),
  /** True when this row is the requesting user. */
  you: z.boolean().default(false),
});
export type LeaderboardEntry = z.infer<typeof LeaderboardEntrySchema>;

export const LeaderboardSchema = z.object({
  enabled: z.boolean(),
  entries: z.array(LeaderboardEntrySchema),
  /** The requesting user's own row when outside the top slice. */
  yourEntry: LeaderboardEntrySchema.nullable(),
});
export type Leaderboard = z.infer<typeof LeaderboardSchema>;
export type ChallengeSession = z.infer<typeof ChallengeSessionSchema>;
